import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Text } from '@actual-app/components/text';
import { View } from '@actual-app/components/view';
import { send } from '@actual-app/core/platform/client/connection';
import type { EstadoPresupuestoCofre } from '@actual-app/core/server/encryption/cofre-app';

import { Boton } from '#components/mobile/ui/Boton';
import { Hoja } from '#components/mobile/ui/Hoja';
import { Icono } from '#components/mobile/ui/Icono';
import { color, texto } from '#components/mobile/ui/tokens';
import { useMetadataPref } from '#hooks/useMetadataPref';
import { useSelector } from '#redux';

/**
 * Al abrir un presupuesto de la nube: trae su llave del cofre y se la pasa a
 * quien lo comparte si aún no la tiene. Si está sin cifrar y es de esta
 * persona, pide cerrarlo con llave (el cifrado es obligatorio).
 */
export function CofrePresupuesto() {
  const { t } = useTranslation();
  const [cloudFileId] = useMetadataPref('cloudFileId');
  const [encryptKeyId] = useMetadataPref('encryptKeyId');
  const [budgetName] = useMetadataPref('budgetName');
  const userId = useSelector(state => state.user.data?.userId);
  const esDuena = useSelector(state =>
    (state.budgetfiles.allFiles ?? []).some(
      archivo =>
        'cloudFileId' in archivo &&
        archivo.cloudFileId === cloudFileId &&
        archivo.owner === userId,
    ),
  );
  const [estado, setEstado] = useState<EstadoPresupuestoCofre | null>(null);
  const [cifrando, setCifrando] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!cloudFileId) {
      return;
    }
    let vigente = true;
    void send('cofre-preparar-presupuesto', { cloudFileId }).then(res => {
      if (vigente) {
        setEstado(res.estado);
      }
    });
    return () => {
      vigente = false;
    };
  }, [cloudFileId, encryptKeyId]);

  if (estado !== 'sin-cifrar' || encryptKeyId || !esDuena) {
    return null;
  }

  async function cifrar() {
    setCifrando(true);
    setError(false);
    const res = await send('cofre-cifrar-presupuesto');
    setCifrando(false);
    if (res.error) {
      setError(true);
      return;
    }
    setEstado('listo');
  }

  return (
    <Hoja
      aria-label={t('Lock your budget')}
      // Obligatorio: no se cierra tocando fuera.
      onCerrar={() => undefined}
    >
      <View style={{ gap: 16, padding: '8px 4px 12px' }}>
        <View
          style={{
            width: 52,
            height: 52,
            borderRadius: 16,
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: color.accentSoft,
            color: color.accent,
          }}
        >
          <Icono nombre="lock" size={26} />
        </View>
        <Text style={{ ...texto.display, fontSize: 22, color: color.fg }}>
          {t('Lock «{{name}}»', { name: budgetName || t('your budget') })}
        </Text>
        <Text style={{ ...texto.cuerpo, fontSize: 15, color: color.fg2 }}>
          {t(
            'Rumbo encrypts your budgets on your phone before uploading them: the server only keeps data it cannot read. It takes a few seconds and nothing you see changes.',
          )}
        </Text>
        <Text style={{ ...texto.secundario, color: color.fg3 }}>
          {t(
            'If you share it, the other person will open it again on their phone with their Rumbo password.',
          )}
        </Text>
        {error && (
          <Text role="alert" style={{ ...texto.cuerpo, color: color.bad }}>
            {t("We couldn't lock it. Check your connection and try again.")}
          </Text>
        )}
        <Boton bloque isDisabled={cifrando} onPress={() => void cifrar()}>
          {cifrando ? t('Locking…') : t('Lock it')}
        </Boton>
      </View>
    </Hoja>
  );
}
